import React from 'react';
import { Grid, Typography, SxProps } from '@mui/material';
import { mergeSx } from '@client/helpers/mui.helpers';
import { NodonTheme } from '@client/style';
import { gridItemLeftStyles, gridItemRightStyles } from './CommentRow.styles';
import UserAvatar from '@features/users/UserAvatar';
import { User } from '@shared/models/user.model';
import { EllipsisText } from '@ui/EllipsisText';

const commentRowContainerSx = {
  justifyContent: 'flex-end',
  width: '100%',
} as const;

const commentAvatarAlignSx = { alignItems: 'flex-start' } as const;

const commentLeftContentAlignSx = {
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
} as const;

const commentAuthorSx = {
  fontWeight: 500,
  lineHeight: 1.15,
  mb: -1.5,
} as const;

const commentSubtitleSx = {
  display: 'block',
  mb: 2,
  mt: 1,
} as const;

const commentRightGridSx = { position: 'relative', top: 8 } as const;

type CommentRowBaseProps = {
  user: User;
  subtitle?: React.ReactNode;
  leftContent?: React.ReactNode;
  rightContent: React.ReactNode;
  containerProps?: {
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    sx?: SxProps;
  };
};

/**
 * Base component for comments & loader.
 * @param param0
 * @returns
 */
export const CommentRowBase: React.FC<CommentRowBaseProps> = ({
  user,
  subtitle,
  leftContent,
  rightContent,
  containerProps,
}) => {
  return (
    <Grid
      container
      {...containerProps}
      sx={mergeSx(commentRowContainerSx, containerProps?.sx)}
    >
      <Grid
        {...gridItemLeftStyles}
        sx={mergeSx(gridItemLeftStyles.sx, commentAvatarAlignSx)}
      >
        <UserAvatar user={user} small mt={0.75} mr={1.5} />
      </Grid>

      <Grid {...gridItemRightStyles} sx={commentRightGridSx}>
        <Typography
          variant="body1"
          color={NodonTheme.palette.neutral.main}
          sx={commentAuthorSx}
        >
          <EllipsisText showTooltip>{user.name}</EllipsisText>
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            color={NodonTheme.palette.neutral.light}
            sx={commentSubtitleSx}
          >
            {subtitle}
          </Typography>
        )}
      </Grid>

      {leftContent && (
        <Grid
          {...gridItemLeftStyles}
          sx={mergeSx(gridItemLeftStyles.sx, commentLeftContentAlignSx)}
        >
          {leftContent}
        </Grid>
      )}

      <Grid {...gridItemRightStyles}>{rightContent}</Grid>
    </Grid>
  );
};
